import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SecurityIcon from '@mui/icons-material/Security';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Base_URL } from '../../Client/apiURL';
import { processAudioUpload } from '../../pte-mock-test/utils/audioProcessing';
import { processSpeakingQuestionHtml } from './utils/speakingQuestionProcessor';
import useIeltsAutosave from './hooks/useIeltsAutosave';

const SILENCE_TIMEOUT_MS = 3000;
const VOLUME_THRESHOLD = 0.035;
const STANDARD_QUESTION_PREP_SECONDS = 2;
const PART_TWO_PREP_SECONDS = 60;
const PART_TWO_RECORD_SECONDS = 120;
const NEXT_VIDEO_PRELOAD_TIMEOUT_MS = 6000;
const FIRST_AVATAR_FRAME_PAUSE_MS = 500;

const ZoomControl = ({ icon, label, danger = false, active = false }) => (
  <Box
    sx={{
      minWidth: { xs: 56, sm: 72 },
      px: { xs: .5, sm: 1 },
      height: 62,
      borderRadius: 1.5,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: .45,
      color: danger ? '#fff' : active ? '#f04444' : '#f4f4f4',
      bgcolor: danger ? '#d93025' : 'transparent',
      opacity: danger ? 1 : .96,
      cursor: 'default',
      userSelect: 'none',
      '& svg': { fontSize: { xs: 20, sm: 23 } },
    }}
  >
    {icon}
    <Typography sx={{ fontSize: { xs: 10, sm: 11 }, lineHeight: 1, fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

export const isSpeakingSimulationReady = (sections = []) => {
  const questions = sections.flatMap((section, sectionIdx) => (section.questions || []).map((question) => ({
    ...question,
    sectionIdx,
  })));
  if (!questions.length) return false;

  return questions.every((question) => {
    const videoUrl = question.avatar_video_url || question.avatarVideoUrl;
    if (!videoUrl) return false;
    const partNumber = Number(question.sectionIdx ?? -1);
    const isPartTwo = partNumber === 1;
    if (isPartTwo) {
      return Boolean(question.image_url || question.imageUrl || question.cueCardImageUrl);
    }
    return true;
  });
};

const formatQuestionText = (question) => (
  question.question_html || question.questionHtml || question.statement || ''
);

const SpeakingSimulationUI = ({
  sections = [],
  onComplete,
  testId,
  groupId,
  fullTestAttemptId,
  onSubmitStart,
  onFullTestPause,
}) => {
  const allQuestions = useMemo(() => sections.flatMap((section, sectionIdx) => (
    (section.questions || []).map((question, questionIdx) => ({
      ...question,
      sectionIdx,
      questionIdx,
      partId: section.id,
      part_name: section.part_name || section.partName || `Part ${sectionIdx + 1}`,
      examiner_notes: section.examiner_notes || section.instructions,
      order_id: question.order_id || question.orderId || questionIdx + 1,
      avatarVideoUrl: question.avatar_video_url || question.avatarVideoUrl,
      cueCardImageUrl: question.image_url || question.imageUrl || question.cueCardImageUrl,
      cueCardText: question.cue_card_text || question.cueCardText,
      answerTimeLimitSeconds: question.answer_time_limit_seconds || question.answerTimeLimitSeconds,
      silenceTimeoutSeconds: question.silence_timeout_seconds || question.silenceTimeoutSeconds,
    }))
  )), [sections]);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState('permission');
  const [message, setMessage] = useState('Join the exam room to begin.');
  const [audioUrls, setAudioUrls] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [joining, setJoining] = useState(false);
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [advanceMode, setAdvanceMode] = useState('automatic');
  const [prepTimeLeft, setPrepTimeLeft] = useState(0);

  const audioStreamRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceFrameRef = useRef(null);
  const maxTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const prepTimerRef = useRef(null);
  const testStartTimeRef = useRef(new Date().toISOString().slice(0, 19).replace('T', ' '));
  const avatarVideoRef = useRef(null);
  const avatarPlayTimerRef = useRef(null);
  const preloadCacheRef = useRef(new Map());
  const mountedRef = useRef(true);
  const submitInFlightRef = useRef(false);
  const finalSubmitCompletedRef = useRef(false);

  const draftIdentity = useMemo(() => ({
    scope: fullTestAttemptId ? 'full' : 'sectional',
    section: 'speaking',
    testId,
    groupId,
    fullTestAttemptId,
  }), [fullTestAttemptId, groupId, testId]);
  const simulationDraftState = useMemo(() => ({
    answers: audioUrls,
    currentStep: { currentIdx, mode: 'simulation', deliveryMode: 'examiner-simulation' },
    timeState: { elapsed, prepTimeLeft },
    audioState: { audioUrls },
  }), [audioUrls, currentIdx, elapsed, prepTimeLeft]);
  const restoreSimulationDraft = useCallback((draft) => {
    const restoredAudioUrls = draft?.audioState?.audioUrls || draft?.answers;
    if (restoredAudioUrls && typeof restoredAudioUrls === 'object' && !Array.isArray(restoredAudioUrls)) {
      setAudioUrls(restoredAudioUrls);
    }
    const restoredIdx = parseInt(draft?.currentStep?.currentIdx, 10);
    if (Number.isFinite(restoredIdx)) {
      setCurrentIdx(Math.max(0, Math.min(restoredIdx, Math.max(allQuestions.length - 1, 0))));
    }
  }, [allQuestions.length]);
  const { clearDraft } = useIeltsAutosave({
    enabled: Boolean(testId),
    identity: draftIdentity,
    draftState: simulationDraftState,
    onRestore: restoreSimulationDraft,
  });

  const currentQuestion = allQuestions[currentIdx];
  const isPartTwo = currentQuestion?.sectionIdx === 1;
  const isPartTwoStageShare = isPartTwo && ['preparation', 'recording', 'uploading', 'transitioning', 'submitting'].includes(phase);
  const cueCardImageUrl = currentQuestion?.cueCardImageUrl;
  const cueCardText = currentQuestion?.cueCardText;

  const preloadVideo = useCallback((url) => {
    if (!url || typeof document === 'undefined') return Promise.resolve(false);

    const cached = preloadCacheRef.current.get(url);
    if (cached) return cached;

    const promise = new Promise((resolve) => {
      const video = document.createElement('video');
      let settled = false;
      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        video.onloadeddata = null;
        video.oncanplay = null;
        video.onerror = null;
        resolve(loaded);
      };

      const timeoutId = setTimeout(() => finish(false), NEXT_VIDEO_PRELOAD_TIMEOUT_MS);
      const finishAndClear = (loaded) => {
        clearTimeout(timeoutId);
        finish(loaded);
      };

      video.preload = 'auto';
      video.playsInline = true;
      video.muted = true;
      video.onloadeddata = () => finishAndClear(true);
      video.oncanplay = () => finishAndClear(true);
      video.onerror = () => finishAndClear(false);
      video.src = url;
      video.load();
    });

    promise.then((loaded) => {
      if (!loaded && preloadCacheRef.current.get(url) === promise) {
        preloadCacheRef.current.delete(url);
      }
    });

    preloadCacheRef.current.set(url, promise);
    return promise;
  }, []);

  const preloadImage = useCallback((url) => {
    if (!url || typeof Image === 'undefined') return;
    const cacheKey = `image:${url}`;
    if (preloadCacheRef.current.has(cacheKey)) return;
    const image = new Image();
    image.src = url;
    preloadCacheRef.current.set(cacheKey, Promise.resolve(true));
  }, []);

  useEffect(() => {
    if (phase !== 'permission' || !currentQuestion) return;

    preloadVideo(currentQuestion.avatarVideoUrl);
    preloadImage(currentQuestion.cueCardImageUrl);
  }, [currentQuestion, phase, preloadImage, preloadVideo]);

  useEffect(() => {
    const nextQuestion = allQuestions[currentIdx + 1];
    if (!nextQuestion) return;

    preloadVideo(nextQuestion.avatarVideoUrl);
    preloadImage(nextQuestion.cueCardImageUrl);
  }, [allQuestions, currentIdx, preloadImage, preloadVideo]);

  useEffect(() => {
    if (cameraVideoRef.current && cameraStreamRef.current) {
      cameraVideoRef.current.srcObject = cameraStreamRef.current;
      cameraVideoRef.current.play?.().catch(() => {});
    }
  }, [cameraReady, phase]);

  useEffect(() => {
    if (avatarPlayTimerRef.current) {
      clearTimeout(avatarPlayTimerRef.current);
      avatarPlayTimerRef.current = null;
    }

    const video = avatarVideoRef.current;
    if (phase === 'avatar' && video) {
      video.pause?.();
      try {
        video.currentTime = 0;
      } catch (error) {
        // Some browsers block seeking before metadata is ready; playback can still start normally.
      }

      const playAvatar = () => {
        if (!mountedRef.current || phase !== 'avatar') return;
        video.play?.().catch(() => {
          setMessage('Tap the examiner video to continue.');
        });
      };

      avatarPlayTimerRef.current = setTimeout(
        playAvatar,
        currentIdx === 0 ? FIRST_AVATAR_FRAME_PAUSE_MS : 0
      );
    }

    return () => {
      if (avatarPlayTimerRef.current) {
        clearTimeout(avatarPlayTimerRef.current);
        avatarPlayTimerRef.current = null;
      }
    };
  }, [currentIdx, phase]);

  useEffect(() => {
    return () => {
      if (avatarPlayTimerRef.current) {
        clearTimeout(avatarPlayTimerRef.current);
        avatarPlayTimerRef.current = null;
      }
    };
  }, []);

  const handleAvatarVideoClick = useCallback(() => {
    if (phase === 'avatar') {
      if (avatarPlayTimerRef.current) {
        clearTimeout(avatarPlayTimerRef.current);
        avatarPlayTimerRef.current = null;
      }

      avatarVideoRef.current?.play?.().catch(() => {
        setMessage('Tap the examiner video to continue.');
      });
    }
  }, [phase]);

  const cleanupRecordingTimers = useCallback(() => {
    if (silenceFrameRef.current) cancelAnimationFrame(silenceFrameRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    if (prepTimerRef.current) clearInterval(prepTimerRef.current);
    silenceFrameRef.current = null;
    maxTimerRef.current = null;
    elapsedTimerRef.current = null;
    prepTimerRef.current = null;
  }, []);

  const stopAllStreams = useCallback(() => {
    cleanupRecordingTimers();
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioStreamRef.current = null;
    cameraStreamRef.current = null;
    setCameraReady(false);
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, [cleanupRecordingTimers]);

  useEffect(() => {
    return () => stopAllStreams();
  }, [stopAllStreams]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submitTest = useCallback(async (nextAudioUrls) => {
    if (submitInFlightRef.current || finalSubmitCompletedRef.current) {
      return;
    }

    submitInFlightRef.current = true;
    finalSubmitCompletedRef.current = true;
    setSubmitting(true);
    setPhase('submitting');
    setMessage('Submitting your speaking test...');
    if (onSubmitStart) onSubmitStart();

    const endTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const partPayloads = sections.map((section, sectionIdx) => {
      const partQuestions = allQuestions.filter((question) => question.sectionIdx === sectionIdx);
      return {
        [`part${sectionIdx + 1}`]: {
          partId: section.id,
          questions: partQuestions.map((question) => ({
            questionId: question.id,
            questionText: formatQuestionText(question),
            audioUrl: nextAudioUrls[question.id] || null,
          })),
          timeTaken: 0,
        },
      };
    });

    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/saveSpeakingTestAttempt`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          testId,
          start_time: testStartTimeRef.current,
          end_time: endTime,
          status: 'pending_evaluation',
          speaking_delivery_mode: 'examiner_simulation',
          ...(groupId ? { groupId } : {}),
          ...(groupId && fullTestAttemptId ? { fullTestAttemptId } : {}),
          ...(groupId ? { is_speaking_optional: localStorage.getItem('is_speaking_optional') === 'true' } : {}),
          ...Object.assign({}, ...partPayloads),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || 'Save failed');
      stopAllStreams();
      clearDraft().catch(() => {});
      if (onComplete) onComplete(data);
    } catch (error) {
      finalSubmitCompletedRef.current = false;
      setMessage(error.message || 'Could not submit your test. Please try again.');
      setPhase('error');
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  }, [allQuestions, fullTestAttemptId, groupId, onComplete, onSubmitStart, sections, stopAllStreams, testId, clearDraft]);

  const finishQuestion = useCallback(async (blob) => {
    cleanupRecordingTimers();
    setPhase('uploading');
    setMessage('Recording finished. Saving your answer...');
    let uploadedUrl = null;

    try {
      if (blob && blob.size > 0) {
        uploadedUrl = await processAudioUpload(blob);
      }
    } catch (error) {
      setMessage('Could not upload your answer. Please check your connection and try again.');
      setPhase('error');
      return;
    }

    const nextAudioUrls = {
      ...audioUrls,
      [currentQuestion.id]: uploadedUrl,
    };
    setAudioUrls(nextAudioUrls);

    if (currentIdx >= allQuestions.length - 1) {
      await submitTest(nextAudioUrls);
      return;
    }

    setPhase('transitioning');
    setMessage('Preparing next question...');

    const nextQuestion = allQuestions[currentIdx + 1];
    await preloadVideo(nextQuestion?.avatarVideoUrl);
    preloadImage(nextQuestion?.cueCardImageUrl);

    if (!mountedRef.current) return;
    setCurrentIdx((idx) => idx + 1);
    setElapsed(0);
    setPrepTimeLeft(0);
    setPhase('avatar');
    setMessage('Examiner is asking the next question...');
  }, [allQuestions, audioUrls, cleanupRecordingTimers, currentIdx, currentQuestion, preloadImage, preloadVideo, submitTest]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const monitorSilence = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const data = new Uint8Array(analyser.fftSize);
    let hasDetectedVoice = false;
    let silenceStartedAt = null;
    const silenceLimit = Number(currentQuestion?.silenceTimeoutSeconds || 0) > 0
      ? Number(currentQuestion.silenceTimeoutSeconds) * 1000
      : SILENCE_TIMEOUT_MS;

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const value = (data[i] - 128) / 128;
        sum += value * value;
      }
      const volume = Math.sqrt(sum / data.length);
      const now = Date.now();

      if (volume > VOLUME_THRESHOLD) {
        hasDetectedVoice = true;
        silenceStartedAt = null;
      } else if (hasDetectedVoice) {
        if (!silenceStartedAt) silenceStartedAt = now;
        if (now - silenceStartedAt >= silenceLimit) {
          stopRecording();
          return;
        }
      }

      silenceFrameRef.current = requestAnimationFrame(tick);
    };

    silenceFrameRef.current = requestAnimationFrame(tick);
  }, [currentQuestion, stopRecording]);

  const startRecording = useCallback(() => {
    if (!audioStreamRef.current || !currentQuestion) return;

    chunksRef.current = [];
    const recorder = new MediaRecorder(audioStreamRef.current);
    recorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      setPhase('uploading');
      setMessage('Recording finished. Saving your answer...');
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      finishQuestion(blob);
    };

    setElapsed(0);
    setPhase('recording');
    setMessage('Speak now. Your answer is being recorded.');
    recorder.start();

    elapsedTimerRef.current = setInterval(() => {
      setElapsed((value) => value + 1);
    }, 1000);

    const maxSeconds = isPartTwo
      ? PART_TWO_RECORD_SECONDS
      : (Number(currentQuestion.answerTimeLimitSeconds || 0) > 0
        ? Number(currentQuestion.answerTimeLimitSeconds)
        : 45);
    maxTimerRef.current = setTimeout(stopRecording, maxSeconds * 1000);
    if (advanceMode === 'automatic' && !isPartTwo) {
      monitorSilence();
    }
  }, [advanceMode, currentQuestion, finishQuestion, isPartTwo, monitorSilence, stopRecording]);

  const handleManualNextQuestion = useCallback(() => {
    if (phase === 'recording') {
      stopRecording();
    }
  }, [phase, stopRecording]);

  const handleJoinExamRoom = useCallback(async () => {
    if (joining || permissionsReady) return;
    setJoining(true);
    setMessage('Waiting for microphone and camera permission...');

    try {
      const combinedStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      const audioTracks = combinedStream.getAudioTracks();
      const videoTracks = combinedStream.getVideoTracks();

      if (!audioTracks.length) {
        throw new Error('Microphone permission is required for the speaking simulation.');
      }

      audioStreamRef.current = new MediaStream(audioTracks);
      if (videoTracks.length) {
        cameraStreamRef.current = new MediaStream(videoTracks);
        setCameraReady(true);
      }

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      audioContext.createMediaStreamSource(audioStreamRef.current).connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      setPermissionsReady(true);
      setPhase('avatar');
      setMessage('Examiner is asking the first question...');
    } catch (error) {
      setPhase('error');
      setMessage(error.message || 'Microphone and camera permission are required for the speaking simulation.');
    } finally {
      setJoining(false);
    }
  }, [joining, permissionsReady]);

  const handleAvatarEnded = () => {
    if (phase !== 'avatar') return;

    const prepSeconds = isPartTwo ? PART_TWO_PREP_SECONDS : STANDARD_QUESTION_PREP_SECONDS;
    setPrepTimeLeft(prepSeconds);
    setPhase('preparation');
    setMessage(`Preparation time. You have ${prepSeconds} seconds to prepare.`);

    if (prepTimerRef.current) clearInterval(prepTimerRef.current);
    let secondsLeft = prepSeconds;
    prepTimerRef.current = setInterval(() => {
      secondsLeft -= 1;
      setPrepTimeLeft(secondsLeft);

      if (secondsLeft <= 0) {
        if (prepTimerRef.current) clearInterval(prepTimerRef.current);
        prepTimerRef.current = null;
        startRecording();
      }
    }, 1000);
  };

  const currentPartLabel = currentQuestion?.examiner_notes || currentQuestion?.part_name || `Part ${currentQuestion?.sectionIdx + 1 || 1}`;

  if (!permissionsReady && phase !== 'avatar' && phase !== 'recording' && phase !== 'uploading' && phase !== 'transitioning' && phase !== 'submitting') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#1f1f1f', color: '#fff', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#111', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>IELTS Speaking Examiner Simulation</Typography>
          <Typography sx={{ fontSize: 14, color: '#cfcfcf' }}>Preview</Typography>
        </Box>

        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 360px' }, gap: 3, p: { xs: 2, md: 4 }, alignItems: 'center' }}>
          <Box sx={{ bgcolor: '#090909', borderRadius: 3, minHeight: { xs: 320, md: 560 }, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)' }}>
            <Box sx={{ textAlign: 'center', px: 3 }}>
              <SecurityIcon sx={{ fontSize: 54, color: '#7db4ff', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Join exam room</Typography>
              <Typography sx={{ color: '#cfcfcf', maxWidth: 520 }}>
                Allow microphone and camera first. The examiner video will start only after permissions are ready.
              </Typography>
              <Box sx={{ mt: 3, mx: 'auto', maxWidth: 560, display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'center', color: '#ffd99a', bgcolor: 'rgba(255, 193, 7, .12)', border: '1px solid rgba(255, 193, 7, .25)', borderRadius: 2, p: 2 }}>
                <VolumeOffIcon />
                <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                  Take this test in a very quiet room. Background noise can affect recording quality, automatic detection, and AI scoring.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ bgcolor: '#2b2b2b', borderRadius: 3, p: 3, border: '1px solid rgba(255,255,255,.08)' }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, mb: 1 }}>Ready to start?</Typography>
            <Typography sx={{ color: '#d8d8d8', mb: 3 }}>
              Your camera is only used for the live preview. We save your audio answers only.
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.25, mb: 3 }}>
              <Box
                onClick={() => setAdvanceMode('automatic')}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `2px solid ${advanceMode === 'automatic' ? '#0b5cff' : 'rgba(255,255,255,.15)'}`,
                  bgcolor: advanceMode === 'automatic' ? 'rgba(11,92,255,.18)' : 'rgba(255,255,255,.04)',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>Automatic mode</Typography>
                <Typography sx={{ color: '#cfcfcf', fontSize: 13 }}>Recommended for quiet rooms. The test moves ahead after silence.</Typography>
              </Box>
              <Box
                onClick={() => setAdvanceMode('manual')}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `2px solid ${advanceMode === 'manual' ? '#0b5cff' : 'rgba(255,255,255,.15)'}`,
                  bgcolor: advanceMode === 'manual' ? 'rgba(11,92,255,.18)' : 'rgba(255,255,255,.04)',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>Manual next mode</Typography>
                <Typography sx={{ color: '#cfcfcf', fontSize: 13 }}>Use this in a noisy room. Press Next Question after answering.</Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleJoinExamRoom}
              disabled={joining}
              sx={{ bgcolor: '#0b5cff', py: 1.4, fontWeight: 800, '&:hover': { bgcolor: '#084bcc' } }}
            >
              {joining ? 'Joining...' : 'Join with mic and camera'}
            </Button>
            {phase === 'error' && (
              <Typography sx={{ color: '#ff8a8a', mt: 2, fontWeight: 600 }}>{message}</Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', maxHeight: '100vh', bgcolor: '#151515', color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>
        {`
          @keyframes screenShareIn {
            from { opacity: 0; transform: scale(.965) translateY(12px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes tileSlideIn {
            from { opacity: 0; transform: translateX(24px) scale(.92); }
            to { opacity: 1; transform: translateX(0) scale(1); }
          }
          @keyframes avatarFadeIn {
            from { opacity: .01; }
            to { opacity: 1; }
          }
        `}
      </style>
      <Box sx={{ height: 56, flexShrink: 0, px: 3, py: 1.5, bgcolor: '#222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 700 }}>IELTS Speaking Examiner Simulation</Typography>
        <Typography sx={{ fontSize: 14, color: '#ddd' }}>{currentPartLabel}</Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1, md: 2 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(220px, 280px)' }, gridTemplateRows: 'minmax(0, 1fr)', gap: 2, overflow: 'hidden' }}>
        <Box sx={{ position: 'relative', bgcolor: '#050505', borderRadius: 2, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, height: '100%', transition: 'all 600ms cubic-bezier(.2,.8,.2,1)' }}>
          {isPartTwoStageShare && (cueCardImageUrl || cueCardText) ? (
            <Box sx={{ width: '100%', height: '100%', minHeight: 0, bgcolor: '#f6f3ea', color: '#222', p: { xs: 1.5, md: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'screenShareIn 650ms cubic-bezier(.2,.8,.2,1)' }}>
              {cueCardImageUrl ? (
                <Box component="img" src={cueCardImageUrl} alt="Cue card" sx={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }} />
              ) : (
                <Box sx={{ maxWidth: 720, fontSize: 20, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: processSpeakingQuestionHtml(cueCardText || formatQuestionText(currentQuestion)) }} />
              )}
              <Box sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'rgba(0,0,0,.72)', color: '#fff', px: 1.5, py: .75, borderRadius: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <ScreenShareIcon fontSize="small" /> Screen sharing
              </Box>
              {phase === 'preparation' && (
                <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(0,0,0,.78)', color: '#fff', px: 2, py: 1, borderRadius: 1.5, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Preparation</Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 900 }}>{prepTimeLeft}s</Typography>
                </Box>
              )}
            </Box>
          ) : currentQuestion?.avatarVideoUrl ? (
            <Box component="video" ref={avatarVideoRef} key={`${currentQuestion.id}-${currentIdx}`} src={currentQuestion.avatarVideoUrl} preload="auto" playsInline onClick={handleAvatarVideoClick} onEnded={handleAvatarEnded} sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: phase === 'avatar' ? 'pointer' : 'default', animation: 'avatarFadeIn 220ms ease-out' }} />
          ) : (
            <Box aria-label="Examiner video area" sx={{ width: '100%', height: '100%', bgcolor: '#050505' }} />
          )}

        </Box>

        <Box sx={{ minHeight: 0, overflow: 'hidden', display: 'grid', gridTemplateRows: isPartTwoStageShare ? 'minmax(150px, 210px) minmax(120px, 170px) auto' : 'minmax(150px, 210px) auto', alignContent: 'start', gap: 2, transition: 'all 600ms cubic-bezier(.2,.8,.2,1)' }}>
          <Box sx={{ position: 'relative', bgcolor: '#050505', borderRadius: 2, overflow: 'hidden', minHeight: 0, aspectRatio: '4 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 600ms cubic-bezier(.2,.8,.2,1)' }}>
            {cameraReady ? (
              <video ref={cameraVideoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
            ) : (
              <Typography sx={{ color: '#bbb' }}>Camera preview off</Typography>
            )}
            <Typography sx={{ position: 'absolute', bottom: 8, left: 10, fontSize: 13, bgcolor: 'rgba(0,0,0,.6)', px: 1, py: .3, borderRadius: .8 }}>You</Typography>
          </Box>

          {isPartTwoStageShare && (
            <Box sx={{ bgcolor: '#050505', borderRadius: 2, overflow: 'hidden', minHeight: 0, aspectRatio: '16 / 9', animation: 'tileSlideIn 620ms cubic-bezier(.2,.8,.2,1)' }}>
              <Box component="video" src={currentQuestion?.avatarVideoUrl} muted playsInline sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: .85 }} />
            </Box>
          )}

          <Box sx={{ bgcolor: '#252525', borderRadius: 2, p: 2, minHeight: 116, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 800, mb: 1, fontSize: 18 }}>{message}</Typography>
            {phase === 'preparation' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: '#ffd99a', fontWeight: 800 }}>
                <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#ffc247', boxShadow: '0 0 0 6px rgba(255,194,71,.14)' }} />
                <Typography sx={{ color: '#ffd99a', fontWeight: 800 }}>Recording starts in {prepTimeLeft}s</Typography>
              </Box>
            )}
            {phase === 'recording' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: '#ff4b4b', fontWeight: 800 }}>
                  <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#ff2f2f', boxShadow: '0 0 0 6px rgba(255,47,47,.16)' }} />
                  <Typography sx={{ color: '#ff6b6b', fontWeight: 800 }}>Recording {elapsed}s</Typography>
                </Box>
                {advanceMode === 'manual' && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleManualNextQuestion}
                    sx={{ mt: 2, bgcolor: '#0b5cff', fontWeight: 800, '&:hover': { bgcolor: '#084bcc' } }}
                  >
                    {currentIdx >= allQuestions.length - 1 ? 'Finish Answer' : 'Next Question'}
                  </Button>
                )}
              </>
            )}
            {(phase === 'permission' || phase === 'uploading' || phase === 'transitioning' || phase === 'submitting') && (
              <CircularProgress size={22} sx={{ color: '#fff', mt: 1 }} />
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ height: 84, flexShrink: 0, bgcolor: '#181818', borderTop: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: .2, sm: .8, md: 1.4 }, px: 2, overflowX: 'auto' }}>
        <ZoomControl icon={<MicIcon />} label="Mute" active={phase === 'recording'} />
        <ZoomControl icon={<VideocamIcon />} label="Stop Video" />
        <ZoomControl icon={<PeopleAltIcon />} label="Participants" />
        <ZoomControl icon={<ChatBubbleOutlineIcon />} label="Chat" />
        <ZoomControl icon={<ScreenShareIcon />} label="Share Screen" />
        <ZoomControl icon={<SentimentSatisfiedAltIcon />} label="Reactions" />
        <ZoomControl icon={<MoreHorizIcon />} label="More" />
        <Box onClick={onFullTestPause || stopAllStreams} sx={{ pointerEvents: submitting ? 'none' : 'auto' }}>
          <ZoomControl icon={<CallEndIcon />} label="Leave" danger />
        </Box>
      </Box>
    </Box>
  );
};

export default SpeakingSimulationUI;
